/**
 * The shape of the screen, before the screen.
 *
 * Every screen used to put the word "Loading" in the middle of an empty page.
 * On a good connection nobody sees it; on a bad one it is indistinguishable
 * from broken, and it throws the layout the moment the data lands.
 *
 * These draw his shapes empty instead — the same squircles in the same places,
 * so the page does not move when it fills. Reduce Motion is on for a lot of
 * people, including this machine, so the shimmer is a slow opacity pulse
 * rather than a travelling gradient, and `motion-reduce` stops it entirely.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-2xl bg-secondary motion-reduce:animate-none ${className}`}
      aria-hidden
    />
  );
}

/** A stack of his card rows — the shape of nearly every list in this app. */
export function SkeletonList({ rows = 3, className = '' }: { rows?: number; className?: string }) {
  return (
    <div
      className={`space-y-3 ${className}`}
      // biome-ignore lint/a11y/useSemanticElements: the semantic stand-in for role="status" is <output>, which is inline by default and carries form-association semantics; this element is the block layout container that holds the placeholder stack, so swapping it would break the vertical rhythm while announcing exactly the same thing.
      role="status"
      aria-busy="true"
    >
      {Array.from({ length: rows }, (_, i) => (
        // A white plate with the design's 24px corners, not a grey slab — the
        // screen these stand in for is white cards on porcelain.
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows generated from a count — the index IS the identity, they hold no data and the list never reorders
          key={i}
          className="h-[4.5rem] w-full animate-pulse rounded-[24px] border border-border bg-card motion-reduce:animate-none"
        />
      ))}
    </div>
  );
}

/** A heading, a line under it, and a list. What most screens open with. */
export function SkeletonScreen({ rows = 3 }: { rows?: number }) {
  return (
    <div
      className="mx-auto w-full max-w-lg space-y-8 py-2"
      // biome-ignore lint/a11y/useSemanticElements: the semantic stand-in for role="status" is <output>, which is inline by default and carries form-association semantics; this element is the centred page-width layout container for the whole placeholder screen, so swapping it would break the layout while announcing exactly the same thing.
      role="status"
      aria-busy="true"
    >
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <SkeletonList rows={rows} />
    </div>
  );
}
